// ModalDetectionArea.tsx (Final Enhanced Version with Full Rectangle Edit/Delete/Save)

import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import Button from '@/shared/components/ui/Button';
import useRoadManagement from '@/shared/hooks/useRoadManagement';
import { useStreamSource } from '@/shared/hooks/useStreamSource';
import { toSnapshotUrl } from '@/shared/utils/stream';

// Types
interface Point {
  x: number;
  y: number;
}

interface Shape extends Array<Point> {}

interface FormData {
  name?: string;
  type?: string;
  site_id?: string | number;
  road_id?: string | number;
  polygons?: string;
  road_name?: string;
  site_name?: string;
}

interface ModalDetectionAreaProps {
  isOpen: boolean;
  onClose: () => void;
  formData?: FormData;
  onSavePolygons?: (polygons: string) => void;
}

interface DraggingState {
  shapeIndex: number;
  pointIndex: number;
}

interface DraggingShapeOffset {
  shapeIndex: number;
  offset: Point[];
}

const ModalDetectionArea: React.FC<ModalDetectionAreaProps> = ({
  isOpen,
  onClose,
  formData,
  onSavePolygons,
}) => {
  // Canvas functionality
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [selectedShape, setSelectedShape] = useState<number | null>(null);
  const [dragging, setDragging] = useState<DraggingState | null>(null);
  const [draggingShapeOffset, setDraggingShapeOffset] = useState<DraggingShapeOffset | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  const [roadIdParams, setRoadIdParams] = useState<string | number | null>(null);
  const [addMode, setAddMode] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [deleteMode, setDeleteMode] = useState<boolean>(false);

  // api/dsh/road/{road_id}
  // roadIdParams is always converted to number inside useRoadManagement via fetchDetailRoadStream
  const { roadDetailStreamUrl } = useRoadManagement({
    roadIdParams: (roadIdParams != null ? Number(roadIdParams) : null) as Parameters<
      typeof useRoadManagement
    >[0]['roadIdParams'],
  });
  const resolveStream = useStreamSource();
  const streamUrl = resolveStream(
    roadDetailStreamUrl?.data as
      | { stream_url?: string; stream_url_proxy?: string }
      | undefined,
  );

  useEffect(() => {
    if (formData?.road_id) {
      setRoadIdParams(formData.road_id);
      // Drop the previous road's frame so a stale image is never shown while
      // the new snapshot loads
      setBackgroundImage(null);
    }
  }, [formData?.road_id]);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!streamUrl) return;
    const img = new Image();
    img.src = toSnapshotUrl(streamUrl);
    img.onload = () => setBackgroundImage(img);
  }, [streamUrl]);

  const draw = useCallback((): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (backgroundImage) {
      ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);
    }
    shapes.forEach((shape, sIdx) => {
      ctx.beginPath();
      ctx.moveTo(shape[0]?.x ?? 0, shape[0]?.y ?? 0);
      shape.forEach((p, i) => {
        if (i !== 0) ctx.lineTo(p.x, p.y);
      });
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
      ctx.fill();
      ctx.strokeStyle = selectedShape === sIdx && editMode ? 'orange' : 'red';
      ctx.lineWidth = 2;
      ctx.stroke();

      if (editMode) {
        shape.forEach((p) => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
          ctx.fillStyle = '#F2F2F2';
          ctx.fill();
          ctx.strokeStyle = 'blue';
          ctx.lineWidth = 0.5;
          ctx.stroke();
        });
      }
    });
  }, [shapes, selectedShape, editMode, backgroundImage]);

  // Redraw whenever draw changes, and again when the modal opens: the canvas
  // only mounts on open, so a redraw is needed even if the image and shapes
  // were already loaded while it was closed.
  useEffect(() => {
    if (!isOpen) return;
    draw();
  }, [draw, isOpen]);

  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>): void => {
    const pos = getMousePos(e);

    for (let s = 0; s < shapes.length; s++) {
      const shapeLen = shapes[s]?.length ?? 0;
      for (let p = 0; p < shapeLen; p++) {
        const point = shapes[s]?.[p];
        if (!point) continue;
        const dx = point.x - pos.x;
        const dy = point.y - pos.y;
        if (dx * dx + dy * dy < 100) {
          if (deleteMode) {
            // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
            const message = new NoticeMessage('정말로 삭제하시겠습니까?', { mode: 'confirm' });
            message.confirmClicked().then(() => {
              setShapes((prev) => prev.filter((_, idx) => idx !== s));
              setSelectedShape(null);
            });
          } else {
            setDragging({ shapeIndex: s, pointIndex: p });
            setSelectedShape(s);
          }
          if (canvasRef.current) {
            canvasRef.current.style.cursor = 'pointer';
          }
          return;
        }
      }

      const currentShape = shapes[s];
      if (!currentShape) continue;
      if (isPointInPolygon(pos, currentShape)) {
        if (deleteMode) {
          // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
          const message = new NoticeMessage('해당 영역을 삭제하시겠습니까?', { mode: 'confirm' });
          message.confirmClicked().then(() => {
            setShapes((prev) => prev.filter((_, idx) => idx !== s));
            setSelectedShape(null);
          });
        } else {
          setSelectedShape(s);
          setDraggingShapeOffset({
            shapeIndex: s,
            offset: currentShape.map((p) => ({ x: pos.x - p.x, y: pos.y - p.y })),
          });
        }
        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'pointer';
        }
        return;
      }
    }
    setSelectedShape(null);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>): void => {
    if (!editMode) return;
    const pos = getMousePos(e);

    if (dragging) {
      setShapes((prev) => {
        const updated = [...prev];
        const shape = updated[dragging.shapeIndex];
        if (shape) {
          shape[dragging.pointIndex] = pos;
        }
        return updated;
      });
    } else if (draggingShapeOffset) {
      setShapes((prev) => {
        const updated = [...prev];
        const shapeIdx = draggingShapeOffset.shapeIndex;
        const offset = draggingShapeOffset.offset;

        const currentShape = updated[shapeIdx];
        if (currentShape) {
          updated[shapeIdx] = currentShape.map((_, i) => ({
            x: pos.x - (offset[i]?.x ?? 0),
            y: pos.y - (offset[i]?.y ?? 0),
          }));
        }
        return updated;
      });
    }
  };

  const handleMouseUp = (): void => {
    setDragging(null);
    setDraggingShapeOffset(null);
  };

  const handleAddClick = (): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rectWidth = 150;
    const rectHeight = 150;
    const centerX = (canvas.width - rectWidth) / 2;
    const centerY = (canvas.height - rectHeight) / 2;

    const newShape: Shape = [
      { x: centerX, y: centerY },
      { x: centerX + rectWidth, y: centerY },
      { x: centerX + rectWidth, y: centerY + rectHeight },
      { x: centerX, y: centerY + rectHeight },
    ];

    setShapes((prev) => [...prev, newShape]);
    setSelectedShape(shapes.length);
    setAddMode(true);
    setEditMode(false);
    setDeleteMode(false);
  };

  const handleEditClick = (): void => {
    setEditMode((prev) => !prev);
    setAddMode(false);
    setDeleteMode(false);
  };

  const handleDeleteClick = (): void => {
    setDeleteMode((prev) => !prev);
    setAddMode(false);
    setEditMode(false);
    setSelectedShape(null);
  };

  const handleModalClose = (): void => {
    setAddMode(false);
    setEditMode(false);
    setDeleteMode(false);
    onClose();
  };

  const handleSaveClick = (): void => {
    if (shapes.length === 0) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('먼저 검지 영역을 설정해주세요.'));
      return;
    }

    // Convert to 1024x576 coordinate system
    const result = shapes.map((shape) =>
      shape.map((p) => [Math.round((p.x / 640) * 1024), Math.round((p.y / 480) * 576)])
    );
    const polygons = JSON.stringify(result);

    // Log the converted coordinates
    console.log('Converted polygons (1024x576):', result);

    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const confirm = new NoticeMessage(t('이 감지 영역을 저장하시겠습니까?'), { mode: 'confirm' });

    confirm.confirmClicked().then(() => {
      if (onSavePolygons) {
        onSavePolygons(polygons);
      }
      onClose();
    });
  };

  const isPointInPolygon = (point: Point, vs: Point[]): boolean => {
    const x = point.x;
    const y = point.y;

    let inside = false;
    for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
      const xi = vs[i]?.x ?? 0;
      const yi = vs[i]?.y ?? 0;
      const xj = vs[j]?.x ?? 0;
      const yj = vs[j]?.y ?? 0;

      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }

    return inside;
  };

  // Re-initialize from formData every time the modal opens so switching to a
  // different event (or reopening the same one with unsaved edits) always
  // starts from a clean state
  useEffect(() => {
    if (!isOpen) return;

    setSelectedShape(null);
    setAddMode(false);
    setEditMode(false);
    setDeleteMode(false);

    if (formData?.polygons) {
      try {
        const parsed = JSON.parse(formData.polygons);
        // Convert from 1024x576 to 640x480
        const formatted: Shape[] = parsed.map((polygon: number[][]) =>
          polygon.map(([x, y]: number[]) => ({
            x: Math.round(((x ?? 0) / 1024) * 640),
            y: Math.round(((y ?? 0) / 576) * 480),
          }))
        );
        setShapes(formatted);
      } catch (err) {
        console.error('Failed to parse polygons', err);
        setShapes([]);
      }
    } else {
      setShapes([]);
    }
  }, [isOpen, formData?.polygons]);

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 w-full h-full bg-black/50 z-1000 py-[65px] px-[10%]">
      <div className="w-full h-full flex flex-col gap-[25px] p-[40px] dark:bg-db-wrap border border-[#D6DBE1] rounded-[5px]">
        <span className="h3bold">
          검지 영역 설정 - [{formData?.site_name || 'null'}] : [{formData?.road_name || 'null'}]
        </span>
        <span>
          {/* <p>
            Debug = site_id: {formData?.site_id || "null"} || site_name:
            {formData?.site_name || "null"} || road_id:
            {formData?.road_id || "null"} || stream_url: {streamUrl || "null"}{" "}
            || polygons : {formData?.polygons || "null"}
          </p> */}
        </span>
        <div className="w-full flex flex-row gap-[20px]">
          <Button
            onClick={handleAddClick}
            customButton={`flex-1 shrink p-[15px] h-[57px] border rounded title1medium ${
              addMode ? 'bg-[#007AFF] text-white' : 'bg-transparent text-white border-[#818181]'
            } cursor-pointer`}
            label={addMode ? '영역 추가' : '영역 추가'}
          />
          <Button
            onClick={handleEditClick}
            customButton={`flex-1 shrink p-[15px] h-[57px] border rounded title1medium ${
              editMode ? 'bg-[#007AFF] text-white' : 'bg-transparent text-white border-[#818181]'
            } cursor-pointer`}
            label={editMode ? '영역 편집 완료' : '영역 편집'}
          />
          <Button
            onClick={handleDeleteClick}
            customButton={`flex-1 shrink p-[15px] h-[57px] border rounded title1medium ${
              deleteMode ? 'bg-[#007AFF] text-white' : 'bg-transparent text-white border-[#818181]'
            } cursor-pointer`}
            label={deleteMode ? '영역 삭제 완료' : '영역 삭제'}
          />
        </div>
        <div className="w-full flex overflow-hidden border border-[#818181] items-center justify-center">
          <canvas
            ref={canvasRef}
            height={480}
            width={640}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseDown={handleMouseDown}
          />
        </div>
        <div className="flex flex-row justify-center gap-[7px]">
          <Button
            customButton={`btn-search ${editMode || deleteMode ? 'cursor-not-allowed' : ''}`}
            label="저장"
            onClick={handleSaveClick}
            disabled={editMode || deleteMode}
          />
          <Button customButton="btn-danger" label="취소" onClick={handleModalClose} />
        </div>
      </div>
    </div>
  );
};

export default ModalDetectionArea;
